import { APPLICATION_CSV, APPLICATION_JSON, APPLICATION_ZIP } from 'utils/defaults';

export type ErrorResponse = {
  message: string;
  status?: number;
  statusText?: string;
};

export type FetchDataResponse<T> = {
  data?: T;
  blob?: Blob;
  headers?: Headers;
  errorResponse?: ErrorResponse;
};

export const httpErrorResponse = (response: Response) => ({
  errorResponse: {
    message: `HTTP error! status: ${response.status}`,
    status: response.status,
    statusText: response.statusText,
  },
});

export const errorResponse = (error: Error | unknown) => ({
  errorResponse: {
    message: error instanceof Error ? error.message : 'An unexpected error occurred',
  },
});

export const handleResponse = async <T>(response: Response): Promise<FetchDataResponse<T>> => {
  if (!response.ok) {
    return httpErrorResponse(response);
  }

  if (response.status === 204) {
    return {};
  }

  const contentType = response.headers.get('Content-Type');
  if (contentType === APPLICATION_CSV || contentType === APPLICATION_ZIP) {
    const blob = await response.blob();
    const headers = response.headers as Headers;
    return { blob: blob, headers: headers };
  } else if (contentType === APPLICATION_JSON) {
    return { data: await response.json() };
  }

  return errorResponse(
    new Error(`Invalid content type received: '${contentType}', must be JSON or CSV`)
  );
};

export const createFetchRequest = <T>(
  method: string,
  headers: Headers,
  requestBody: T | null
): RequestInit => {
  const normalizedHeaders = Object.fromEntries(
    Array.from(headers.entries()).map(([key, value]) => [key.toLowerCase(), value])
  );

  const init: RequestInit = {
    method,
    headers: {
      ...normalizedHeaders,
      'Content-Type': APPLICATION_JSON,
    },
  };
  if (requestBody) {
    init.body = JSON.stringify(requestBody);
  }
  return init;
};
